// WelcomeDashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import { useNavigate } from 'react-router-dom'
import HighchartsReact from "highcharts-react-official";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { Uzbek } from "flatpickr/dist/l10n/uz.js";
import { Russian } from "flatpickr/dist/l10n/ru.js";
import { PGET } from "helpers/api";
import Card from "./Card";
import { useTranslation } from "react-i18next";
import { Clock3, FileDown, FileUp } from "lucide-react";

/** --- utils --- */
function addDays(date, days) {
	const result = new Date(date);
	result.setDate(result.getDate() + days);
	return result;
}
function formatDate(date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}
const fmtMoney = (n) =>
	(Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
		maximumFractionDigits: 0,
	});

const DebtCard = ({ debt }) => {
	const debtRows = [
		{
			label: "Berilgan",
			sum: debt.debtSum,
			usd: debt.debtUsd,
			color: "#d60000",
			bg: "#fde9e9",
			icon: <FileDown size={18} strokeWidth={2.8} />,
		},
		{
			label: "Qaytarilgan",
			sum: debt.paidSum,
			usd: debt.paidUsd,
			color: "#0eac3d",
			bg: "#e4f8ea",
			icon: <FileUp size={18} strokeWidth={2.8} />,
		},
	];

	return (
		<div
			style={{
				position: "relative",
				background: "#fff",
				borderRadius: "12px",
				padding: "20px 18px",
				borderTop: "4px solid #ff9900",
				boxShadow: "0 4px 10px rgba(0,0,0,0.07)",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				minHeight: "100%",
				transition: "all 0.25s ease",
				cursor: "pointer",
			}}
			onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
			onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0px)")}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: "10px",
					paddingBottom: "12px",
					borderBottom: "1px dashed #ddd",
				}}
			>
				<h4 style={{ margin: 0, color: "#122039", fontSize: "13px", fontWeight: 700 }}>
					Qarzdorlik
				</h4>
				<div
					style={{
						width: "28px",
						height: "28px",
						borderRadius: "8px",
						background: "#ff9900",
						color: "#fff",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					<Clock3 size={18} strokeWidth={2.8} />
				</div>
			</div>

			<div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "12px" }}>
				{debtRows.map((row, index) => (
					<div
						key={row.label}
						style={{
							display: "flex",
							gap: "10px",
							alignItems: "center",
							paddingTop: index === 0 ? 0 : "10px",
							borderTop: index === 0 ? "none" : "1px solid #edf0f5",
						}}
					>
						<div
							style={{
								width: "34px",
								height: "34px",
								borderRadius: "50%",
								background: row.bg,
								color: row.color,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
							}}
						>
							{row.icon}
						</div>
						<div style={{ minWidth: 0, flex: 1 }}>
							<div style={{ color: "#122039", fontSize: "12px", fontWeight: 600 }}>
								{row.label}
							</div>
							<div
								style={{
									color: row.color,
									fontSize: "16px",
									
									lineHeight: 1.35,
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
								title={`${fmtMoney(row.sum)} so'm | $${fmtMoney(row.usd)}`}
							>
								{fmtMoney(row.sum)} <span style={{ color: "#6f7b91", fontSize: "11px" }}>so'm</span>
								{" | $"}
								{fmtMoney(row.usd)}
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

function getDateRangeByFilter(filter) {
	const end = new Date();
	const start = addDays(end, filter.days);
	const formattedStart = formatDate(start);
	return {
		startDate: formattedStart,
		endDate: filter.key === "yesterday" ? formattedStart : formatDate(end)
	};
}

/** --- component --- */
export default function WelcomeDashboard() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const today = new Date();

	/** Filters (flat i18n keys) */
	const filters = useMemo(
		() => [
			{ key: "today", label: t("FILTER_TODAY"), days: 0 },
			{ key: "yesterday", label: t("FILTER_YESTERDAY"), days: -1 },
			{ key: "week", label: t("FILTER_WEEK"), days: -6 },
			{ key: "month", label: t("FILTER_MONTH"), days: -30 },
			{ key: "quarter", label: t("FILTER_QUARTER"), days: -90 },
		],
		[i18n.language, t]
	);

	/** Date range state */
	const [activeFilter, setActiveFilter] = useState("week");
	const [dateRange, setDateRange] = useState({
		startDate: formatDate(addDays(today, -6)),
		endDate: formatDate(today),
	});

	/** POS select state (start with “all” option to avoid uncontrolled <Select>) */
	const ALL_POS_OPTION = useMemo(
		() => ({ value: "all", label: t("FILTER_ALL_POS") }),
		[t]
	);
	const [posses, setPosses] = useState([ALL_POS_OPTION]);
	const [pos, setPos] = useState(ALL_POS_OPTION);

	/** API data */
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	// “kpi” holds sales/profit/expenses (kept name to avoid wider refactor)
	const [kpi, setKpi] = useState({
		total: 0,          // Net sales = grossSales - returnedAmount
		grossSales: 0,     // Gross sales before returns
		returnedAmount: 0, // Value of returned goods
		cash: 0,           // Sales split: cash
		terminal: 0,       // Sales split: terminal
		others: 0,         // Sales split: others (click/payme/...)
		debt: 0,           // Sales split: sold on debt (qarzga sotildi)
		debtReturned: 0,   // Debt repaid in period (qarz qaytarildi)
		profit: 0,         // Profit
		expense: 0,        // Expenses
	});

	const [debt, setDebt] = useState({
		debtSum: 0,
		debtUsd: 0,
		paidSum: 0,
		paidUsd: 0,
	});
	const [salesTrend, setSalesTrend] = useState({ categories: [], data: [] });
	const [paymentShare, setPaymentShare] = useState({
		cash: 0,
		terminal: 0,
		debt: 0,
	});
	const [topProducts, setTopProducts] = useState({
		categories: [],
		data: [],
	});
	const [profitExpense, setProfitExpense] = useState({
		categories: [],
		profit: [],
		expense: [],
	});
	const [cashiers, setCashiers] = useState({ categories: [], data: [] });

	/** Load POS list (safe) */
	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const res = await PGET("/services/web/api/pos-pageList", {}, { loader: true });

				if (res === undefined) {
					navigate('/documents-in');
					return;
				}
				const raw = res?.data ?? res ?? [];
				const formatted = Array.isArray(raw)
					? raw.map((p) => ({
						value: p.id ?? p.value,
						label: p.name ?? p.label ?? String(p.id ?? p.value),
					}))
					: [];
				const withAll = [ALL_POS_OPTION, ...formatted];
				if (!mounted) return;
				setPosses(withAll);
				// if current pos is not in list anymore, reset to ALL
				const exists = withAll.some((o) => o.value === pos?.value);
				if (!exists) setPos(ALL_POS_OPTION);
			} catch (e) {
				if (!mounted) return;
				setPosses([ALL_POS_OPTION]);
				setPos(ALL_POS_OPTION);
			}
		})();
		return () => {
			mounted = false;
		};
	}, [ALL_POS_OPTION]); // re-run when ALL_POS_OPTION label changes (language change)

	   /** Flatpickr (React component) */
	   const getLocale = () => {
		 if (i18n.language === "ru" || i18n.language === "ru-RU") return Russian;
		 return Uzbek;
	   };
	   const flatpickrValue = dateRange.startDate && dateRange.endDate
		 ? [dateRange.startDate, dateRange.endDate]
		 : [];

	/** Derive label */
	const periodLabel = useMemo(() => {
		const f = filters.find((f) => f.key === activeFilter);
		return f ? f.label : t("FILTER_CUSTOM_RANGE");
	}, [filters, activeFilter, t]);

	/** Sync date range when filter changes */
	useEffect(() => {
		if (!activeFilter) return;
		const f = filters.find((x) => x.key === activeFilter);
		if (!f) return;
		setDateRange(getDateRangeByFilter(f));
	}, [activeFilter, filters]);

	/** Auto-load on first open with the default (one week) range */
	const [showDashboard, setShowDashboard] = useState(false);
	const fetchDashboardData = async (range = dateRange) => {
		const { startDate, endDate } = range;
		// Don't fire requests with missing dates (avoids 400 on required params)
		if (!startDate || !endDate) return;
		setLoading(true);
		setError("");
		try {
			const posId = pos?.value === "all" ? undefined : pos?.value;
			const query = { startDate, endDate, posId };

			const [kpiRes, trendRes, shareRes, productsRes, peRes, cashiersRes, debtRes] =
				await Promise.all([
					PGET("/services/web/api/dashboard/kpi", query),
					PGET("/services/web/api/dashboard/sales-trend", query),
					PGET("/services/web/api/dashboard/payment-share", query),
					PGET("/services/web/api/dashboard/top-products", query),
					PGET("/services/web/api/dashboard/profit-expense", query),
					PGET("/services/web/api/dashboard/cashiers", query),
					PGET("/services/web/api/dashboard-client-history-debt", query)
				]);
			const debtList = debtRes?.data?.debtList || [];
			const paidList = debtRes?.data?.paidList || [];
			const d = {
				debtSum: debtList.find((x) => x.currencyName === "So'm")?.debtAmount || 0,
				debtUsd: debtList.find((x) => x.currencyName === "USD")?.debtAmount || 0,
				paidSum: paidList.find((x) => x.currencyName === "So'm")?.debtAmount || 0,
				paidUsd: paidList.find((x) => x.currencyName === "USD")?.debtAmount || 0,
			};

			setDebt(d);
			const k = kpiRes?.data ?? kpiRes ?? {};
			setKpi({
				total: Number(k.total || 0),
				grossSales: Number(k.grossSales || 0),
				returnedAmount: Number(k.returnedAmount || 0),
				cash: Number(k.cash || 0),
				terminal: Number(k.terminal || 0),
				others: Number(k.others || 0),
				debt: Number(k.debt || 0),
				debtReturned: Number(k.debtReturned || 0),
				profit: Number(k.profit || 0),
				expense: Number(k.expense || 0),
			});

			const tr = trendRes?.data ?? trendRes ?? {};
			setSalesTrend({
				categories: Array.isArray(tr.categories) ? tr.categories : [],
				data: Array.isArray(tr.data) ? tr.data : [],
			});

			const sh = shareRes?.data ?? shareRes ?? {};
			setPaymentShare({
				cash: Number(sh.cash || 0),
				terminal: Number(sh.terminal || 0),
				debt: Number(sh.debt || 0),
			});

			const tp = productsRes?.data ?? productsRes ?? {};
			setTopProducts({
				categories: Array.isArray(tp.categories) ? tp.categories : [],
				data: Array.isArray(tp.data) ? tp.data : [],
			});

			const pe = peRes?.data ?? peRes ?? {};
			setProfitExpense({
				categories: Array.isArray(pe.categories) ? pe.categories : [],
				profit: Array.isArray(pe.profit) ? pe.profit : [],
				expense: Array.isArray(pe.expense) ? pe.expense : [],
			});

			const cs = cashiersRes?.data ?? cashiersRes ?? {};
			setCashiers({
				categories: Array.isArray(cs.categories) ? cs.categories : [],
				data: Array.isArray(cs.data) ? cs.data : [],
			});
			setShowDashboard(true);
		} catch (e) {
			console.error(e);
			setError(t("ERR_LOAD_FAILED"));
		} finally {
			setLoading(false);
		}
	};

	// dateRange yoki pos o'zgarganda va showDashboard true bo'lsa, fetchDashboardData chaqiriladi
	useEffect(() => { if (!showDashboard) return; fetchDashboardData(); }, [dateRange, pos]);

	/** Chart options (always safe arrays) */
	const salesLine = useMemo(
		() => ({
			chart: { type: "line", backgroundColor: "transparent" },
			title: { text: `${t("CHART_SALES_CHANGE")} (${periodLabel})`, style: { fontSize: "13px" } },
			xAxis: { categories: salesTrend.categories || [] },
			yAxis: { title: { text: t("CHART_CURRENCY_SOM") } },
			series: [{ name: t("CHART_SALES_AMOUNT"), data: salesTrend.data || [] }],
			credits: { enabled: false },
		}),
		[salesTrend, periodLabel, t]
	);

	const paymentPie = useMemo(
		() => ({
			chart: { type: "pie", backgroundColor: "transparent" },
			title: { text: `${t("CHART_PAYMENT_SHARE")} (${periodLabel})`, style: { fontSize: "13px" } },
			series: [
				{
					name: t("CHART_SHARE"),
					colorByPoint: true,
					data: [
						{ name: t("CHART_CASH"), y: paymentShare.cash || 0 },
						{ name: t("CHART_TERMINAL"), y: paymentShare.terminal || 0 },
						{ name: t("CHART_DEBT"), y: paymentShare.debt || 0 },
					],
				},
			],
			credits: { enabled: false },
		}),
		[paymentShare, periodLabel, t]
	);

	const productColumns = useMemo(
		() => ({
			chart: { type: "column", backgroundColor: "transparent" },
			title: { text: `${t("CHART_TOP_PRODUCTS")} (${periodLabel})`, style: { fontSize: "13px" } },
			xAxis: { categories: topProducts.categories || [] },
			yAxis: { title: { text: t("CHART_QTY_SOLD") } },
			series: [{ name: t("CHART_QUANTITY"), data: topProducts.data || [] }],
			credits: { enabled: false },
		}),
		[topProducts, periodLabel, t]
	);

	const profitAndExpense = useMemo(
		() => ({
			chart: { type: "areaspline", backgroundColor: "transparent" },
			title: { text: `${t("CHART_PROFIT_EXPENSE")} (${periodLabel})`, style: { fontSize: "13px" } },
			xAxis: { categories: profitExpense.categories || [] },
			yAxis: { title: { text: t("CHART_CURRENCY_SOM") } },
			series: [
				{ name: t("CHART_PROFIT"), data: profitExpense.profit || [] },
				{ name: t("CHART_EXPENSE"), data: profitExpense.expense || [] },
			],
			credits: { enabled: false },
		}),
		[profitExpense, periodLabel, t]
	);

	// You can replace this “bar” with your own dataset if needed
	const byPosesBar = useMemo(
		() => ({
			chart: { type: "bar", backgroundColor: "transparent" },
			title: { text: `${t("CHART_AVG_SHOP_SALES")} (${periodLabel})`, style: { fontSize: "13px" } },
			xAxis: { categories: salesTrend.categories || [] },
			yAxis: { title: { text: t("CHART_SALES_MLN_SOM") } },
			series: [{ name: t("CHART_AVG_SALES"), data: salesTrend.data || [] }],
			credits: { enabled: false },
		}),
		[salesTrend, periodLabel, t]
	);

	const cashierOptions = useMemo(
		() => ({
			chart: { type: "column", backgroundColor: "transparent" },
			title: { text: `${t("CHART_CASHIERS_SALES")} (${periodLabel})`, style: { fontSize: "13px" } },
			xAxis: { categories: cashiers.categories || [] },
			yAxis: { title: { text: t("CHART_CURRENCY_SOM") } },
			series: [{ name: t("CHART_SALES_AMOUNT"), colorByPoint: true, data: cashiers.data || [] }],
			credits: { enabled: false },
		}),
		[cashiers, periodLabel, t]
	);

	return (
		<>
			{!showDashboard && (
				<div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", margin: "40px 0" }}>
					<button
						id="show-report-btn"
						style={{
							background: "#00b894",
							color: "#fff",
							border: "none",
							borderRadius: "6px",
							padding: "12px 40px",
							fontWeight: 600,
							cursor: "pointer",
							boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
							transition: "transform 0.1s",
						}}
						onClick={() => fetchDashboardData()}
						disabled={loading}
					>
						{loading ? t("PLEASE_WAIT") : t("SHOW_REPORT_BTN", "Hisobotni ko‘rish")}
					</button>
				</div>
			)}
			{showDashboard && !loading && (
				<div style={{ background: "#f5f7fa", minHeight: "100vh", padding: "25px 0" }}>
					{/* 🧪 Testing mode notice (ALWAYS visible at the top) */}
					<div
						style={{
							background: "#fff3cd",
							border: "1px solid #ffeeba",
							color: "#856404",
							padding: "10px 15px",
							borderRadius: "6px",
							marginBottom: "20px",
							fontSize: "13px",
							fontWeight: 500,
						}}
					>
						{t("TESTING_MODE_NOTICE")}
					</div>

					{/* Header */}
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							flexWrap: "wrap",
							gap: "10px",
							marginBottom: "25px",
						}}
					>
						{/* POS Select */}
						<Select
							options={posses}
							value={pos}
							onChange={setPos}
							placeholder={t("FILTER_SELECT_POS")}
							styles={{
								control: (b) => ({
									...b,
									minHeight: "34px",
									fontSize: "12px",
									borderColor: "#ccc",
									width: "260px",
								}),
							}}
						/>

						{/* Filters */}
						<div style={{ display: "flex", gap: "5px" }}>
							{filters.map((f) => (
								<button
									key={f.key}
									onClick={() => setActiveFilter(f.key)}
									style={{
										background: activeFilter === f.key ? "#0984e3" : "#fff",
										color: activeFilter === f.key ? "#fff" : "#333",
										border: "1px solid #ccc",
										borderRadius: "5px",
										padding: "4px 10px",
										fontSize: "12px",
									}}
								>
									{f.label}
								</button>
							))}
						</div>

											 {/* Flatpickr */}
											 <Flatpickr
												 options={{
													 mode: "range",
													 dateFormat: "Y-m-d",
													 locale: getLocale(),
												 }}
												 value={flatpickrValue}
												 onChange={dates => {
													 if (dates.length === 2) {
														 setDateRange({
															 startDate: formatDate(dates[0]),
															 endDate: formatDate(dates[1])
														 });
													 }
												 }}
												 placeholder={t("SELECT_DATE_RANGE", "Sana oralig'ini tanlang")}
												 style={{
													 width: "200px",
													 border: "1px solid #ccc",
													 borderRadius: "6px",
													 padding: "4px 8px",
													 fontSize: "12px",
													 background: "#fff",
													 cursor: "pointer",
												 }}
											 />

						{/* Hisobotni ko‘rish button */}

					</div>

					{/* Errors & Loading */}
					{error && <div style={{ color: "#d63031", marginBottom: 10 }}>{error}</div>}

					{/* Dashboard content only after button click and data loaded */}

					<>
						{/* Summary Cards */}
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
								gap: "15px",
								marginBottom: "25px",
							}}
						>
							<Card
								color="#0984e3"
								title={t("CARD_SALES_TOTAL")}
								value={kpi.total}
								icon="💰"
								sections={[
									{
										title: t("CARD_SALES_SECTION_SOLD"),
										items: [
											{ label: t("CARD_SALES_GROSS"), value: `${fmtMoney(kpi.grossSales)} ${t("COMMON_SOM")}` },
											{ label: t("CARD_SALES_RETURNED"), value: `${fmtMoney(kpi.returnedAmount)} ${t("COMMON_SOM")}` },
										],
									},
									{
										title: t("CARD_SALES_SECTION_COLLECTED"),
										items: [
											{ label: t("CARD_SALES_CASH"), value: `${fmtMoney(kpi.cash)} ${t("COMMON_SOM")}` },
											{ label: t("CARD_SALES_TERMINAL"), value: `${fmtMoney(kpi.terminal)} ${t("COMMON_SOM")}` },
											{ label: t("CARD_SALES_OTHERS"), value: `${fmtMoney(kpi.others)} ${t("COMMON_SOM")}` },
											{
												label: t("CARD_SALES_COLLECTED_TOTAL"),
												value: `${fmtMoney(kpi.cash + kpi.terminal + kpi.others)} ${t("COMMON_SOM")}`,
												emphasize: true,
											},
										],
									},
									{
										title: t("CARD_SALES_SECTION_DEBT"),
										items: [
											{ label: t("CARD_SALES_DEBT"), value: `${fmtMoney(kpi.debt)} ${t("COMMON_SOM")}` },
											{ label: t("CARD_SALES_DEBT_RETURNED"), value: `${fmtMoney(kpi.debtReturned)} ${t("COMMON_SOM")}` },
										],
									},
								]}
							/>

							<Card color="#00b894" title={t("CARD_PROFIT")} value={kpi.profit} icon="📈" />
							<Card color="#d63031" title={t("CARD_EXPENSES")} value={kpi.expense} icon="💸" />
						<DebtCard debt={debt} />
						</div>


						{/* Charts */}
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
								gap: "30px",
							}}
						>
							<ChartCard options={salesLine} />
							<ChartCard options={paymentPie} />
							<ChartCard options={productColumns} />
							<ChartCard options={profitAndExpense} />
							<ChartCard options={byPosesBar} />
							<ChartCard options={cashierOptions} />
						</div>
					</>
				</div>
			)}
		</>
	);

}

/** Helper */
const ChartCard = ({ options }) => (
	<div
		style={{
			background: "#fff",
			borderRadius: "8px",
			boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
			padding: "10px",
			minHeight: "280px",
		}}
	>
		<HighchartsReact highcharts={Highcharts} options={options} />
	</div>
);
